import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';

export default function MaintenancePage() {
  const navigate = useNavigate();
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [endTime, setEndTime] = useState<Date | null>(null);

  useEffect(() => {
    checkMaintenanceStatus();
  }, []);

  useEffect(() => {
    if (!endTime) return;

    const interval = setInterval(() => {
      const now = new Date();
      const diff = endTime.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining('Maintenance terminée');
        // Recharger la page pour vérifier si la maintenance est désactivée
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeRemaining(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    }, 1000);

    return () => clearInterval(interval);
  }, [endTime]);

  const checkMaintenanceStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('maintenance_mode')
        .select('*')
        .eq('id', '00000000-0000-0000-0000-000000000001')
        .single();

      if (error) {
        console.error('Erreur vérification maintenance:', error);
        return;
      }

      if (data && data.is_active && data.end_time) {
        setEndTime(new Date(data.end_time));
      } else if (data && !data.is_active) {
        // La maintenance est désactivée, rediriger vers l'accueil
        navigate('/', { replace: true });
      }
    } catch (err) {
      console.error('Erreur inattendue:', err);
    }
  };

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Connexion de l'admin
      let authResult;
      if (loginMethod === 'email') {
        authResult = await supabase.auth.signInWithPassword({
          email: identifier,
          password: password,
        });
      } else {
        authResult = await supabase.auth.signInWithPassword({
          phone: identifier,
          password: password,
        });
      }

      const { data, error: authError } = authResult;

      if (authError) {
        setError('Identifiants incorrects');
        setLoading(false);
        return;
      }

      if (!data.user) {
        setError('Aucun utilisateur trouvé');
        setLoading(false);
        return;
      }

      // Vérifier si c'est un super admin
      const { data: adminData, error: adminError } = await supabase
        .from('super_admins')
        .select('*')
        .eq('user_id', data.user.id)
        .single();

      if (adminError || !adminData) {
        setError('Accès refusé. Seuls les administrateurs peuvent se connecter.');
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      // Admin vérifié, rediriger vers la page de configuration
      navigate('/admin/configuration');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la connexion');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Logo et nom de la clinique */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src="https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/brand/clinic.jpeg"
              alt="Clinique Sainte Famille"
              className="h-24 w-24 md:h-32 md:w-32 rounded-full object-cover shadow-xl border-4 border-white"
            />
          </div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-teal-700 mb-2">
            Clinique Sainte Famille
          </h1>
          <p className="text-sm md:text-base text-gray-600">
            Votre santé, notre priorité
          </p>
        </div>

        {/* Carte principale */}
        <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 border-2 border-teal-100">
          {/* Icône de maintenance */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg">
              <i className="ri-tools-line text-3xl md:text-4xl text-white"></i>
            </div>
          </div>

          {/* Titre */}
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-4">
            Maintenance en cours
          </h2>

          {/* Message */}
          <p className="text-center text-sm md:text-base text-gray-600 mb-6 leading-relaxed">
            Notre site est actuellement en maintenance pour vous offrir une meilleure expérience.
            Nous serons de retour très bientôt.
          </p>

          {/* Compteur */}
          {endTime && timeRemaining ? (
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 mb-6 border-2 border-teal-200">
              <p className="text-center text-sm text-gray-600 mb-3">
                Temps restant estimé
              </p>
              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-xl shadow-md">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                  <span className="text-3xl md:text-4xl font-bold text-teal-700 font-mono">
                    {timeRemaining}
                  </span>
                </div>
              </div>
            </div>
          ) : (endTime === null && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 mb-6 border-2 border-amber-200">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-xl shadow-md">
                  <i className="ri-time-line text-2xl text-amber-600"></i>
                  <span className="text-lg md:text-xl font-bold text-amber-700">
                    Durée indéterminée
                  </span>
                </div>
              </div>
            </div>
          ))}

          {/* Informations de contact */}
          <div className="bg-teal-50 rounded-2xl p-4 md:p-6 border-2 border-teal-100">
            <h3 className="font-bold text-base md:text-lg text-teal-800 mb-3 text-center">
              <i className="ri-phone-line mr-2"></i>
              Besoin d'aide ?
            </h3>
            <div className="space-y-2 text-sm md:text-base">
              <div className="flex items-center justify-center gap-2 text-gray-700">
                <i className="ri-phone-fill text-teal-600"></i>
                <span className="font-medium">Standard :</span>
                <a href="tel:+22901954058 91" className="text-teal-600 hover:text-teal-700 font-semibold">
                  +229 01 95 40 58 91
                </a>
              </div>
              <div className="flex items-center justify-center gap-2 text-gray-700">
                <i className="ri-phone-fill text-red-500"></i>
                <span className="font-medium">Urgences :</span>
                <a href="tel:+22901942754 24" className="text-red-600 hover:text-red-700 font-semibold">
                  +229 01 94 27 54 24
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bouton admin discret */}
        {!showAdminLogin && (
          <div className="text-center mt-6">
            <button
              onClick={() => setShowAdminLogin(true)}
              className="text-xs text-gray-400 hover:text-teal-600 transition-colors"
            >
              Accès administrateur
            </button>
          </div>
        )}

        {/* Formulaire de connexion admin */}
        {showAdminLogin && (
          <div className="mt-6 bg-white rounded-2xl shadow-xl p-6 border-2 border-teal-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                Connexion administrateur
              </h3>
              <button
                onClick={() => setShowAdminLogin(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email ou Téléphone
                </label>
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
                  placeholder="admin@example.com ou +229..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Connexion...
                  </>
                ) : (
                  <>
                    <i className="ri-login-box-line mr-2"></i>
                    Se connecter
                  </>
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
